import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatMenuTrigger } from "@angular/material/menu";

import { TextConstants } from "@shared/constants";
import { EntityTypeEnum } from "@shared/enums";
import { IAdvancedSearchFilterOption } from "@shared/interfaces";
import { LocalizationUtils } from "@shared/utils";

import { InputChipsComponent } from "../inputs/input-chips/input-chips.component";

export interface IAdvancedSearchFilter {
  label: string;
  options: IAdvancedSearchFilterOption[];
  outputValuesPropertyName: string;
  optionsFormGroup?: UntypedFormGroup;
  enabled?: boolean;
  enabledOrder?: number;
}

export interface IAdvancedSearchConfig {
  entityType: EntityTypeEnum;
  filters: IAdvancedSearchFilter[];
  outputSearchPropertyName: string;
}

@Component({
  standalone: false,
  selector: "app-advanced-search",
  templateUrl: "./advanced-search.component.html",
  styleUrls: ["./advanced-search.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedSearchComponent implements OnInit {
  config = input.required<IAdvancedSearchConfig>();

  initialSearch = input<any>(undefined);

  searchChanged = output<any>();

  @ViewChildren("filterOptionsMenuTriggers") filterOptionsMenuTriggers: QueryList<MatMenuTrigger>;

  @ViewChild("inputChips") inputChips: InputChipsComponent;

  private readonly cdr = inject(ChangeDetectorRef);

  readonly translations: any = {
    remove: TextConstants.REMOVE,
    search: `${TextConstants.SEARCH}...`,
  };

  searchPlaceHolder = signal<string>("");

  isAnyFilterEnabled = signal(false);

  areAllFiltersEnabled = signal(false);

  selectedFilter = signal<IAdvancedSearchFilter>(undefined);

  currentSearchText = signal<string>("");

  ngOnInit(): void {
    this.searchPlaceHolder.set(
      `${TextConstants.SEARCH} ${LocalizationUtils.getEntityType(this.config().entityType, true, "lowercase")}...`,
    );
    this.setInitialSearch();
  }

  onSearch = (value: string): void => {
    this.currentSearchText.set(value.trim());
    this.emitSearch();
  };

  onAddFilter = (filter: IAdvancedSearchFilter, isSelectFilter = true): void => {
    filter.enabled = true;
    filter.enabledOrder =
      Math.max(
        ...this.config()
          .filters.filter((f) => f.enabled)
          .map((f) => f.enabledOrder ?? 0),
      ) + 1;
    this.isAnyFilterEnabled.set(true);
    this.setAreAllFiltersEnabled();
    if (isSelectFilter) {
      this.onSelectFilter(filter, true);
    }
  };

  onRemoveFilter = (filter: IAdvancedSearchFilter): void => {
    const hadAnyOptionEnabled = filter.options.some((o) => o.enabled);

    filter.enabled = false;
    filter.enabledOrder = undefined;
    filter.options.forEach((o) => (o.enabled = false));
    filter.optionsFormGroup = undefined;
    this.isAnyFilterEnabled.set(this.config().filters.some((f) => f.enabled));
    this.setAreAllFiltersEnabled();
    this.selectedFilter.set(undefined);
    if (hadAnyOptionEnabled) {
      this.emitSearch();
    }
  };

  onSelectFilter = (filter: IAdvancedSearchFilter, isOpenFilterOptions = false): void => {
    this.selectedFilter.set(undefined);
    this.cdr.detectChanges();
    if (!filter.optionsFormGroup) {
      filter.optionsFormGroup = this.getOptionsFormGroup(undefined);
    }

    this.selectedFilter.set(filter);
    if (isOpenFilterOptions) {
      this.cdr.detectChanges();
      setTimeout(() => {
        this.filterOptionsMenuTriggers.last.openMenu();
        this.cdr.detectChanges();
      }, 1);
    }

    setTimeout(() => {
      this.inputChips.inputElementRef.nativeElement.focus();
    }, 100);
  };

  onToggleFilterOption = (option: any): void => {
    option.enabled = !option.enabled;
    this.emitSearch();
  };

  getEnabledSortedFilters = (): IAdvancedSearchFilter[] =>
    this.config()
      .filters.filter((f) => f.enabled)
      .sort((a, b) => (a.enabledOrder > b.enabledOrder ? 1 : -1));

  getFilterOptionsEnabled = (filter: IAdvancedSearchFilter): IAdvancedSearchFilterOption[] =>
    filter.options.filter((o) => o.enabled);

  private setInitialSearch = (): void => {
    if (!this.initialSearch()) {
      return;
    }

    const outputSearchPropertyName = this.config().outputSearchPropertyName;
    const initialSearchText = this.initialSearch()[outputSearchPropertyName] || "";

    this.currentSearchText.set(initialSearchText);

    for (const initialSearchEnabledFilterPropertyName in this.initialSearch()) {
      if (initialSearchEnabledFilterPropertyName === outputSearchPropertyName) {
        continue;
      }

      const filter = this.config().filters.find(
        (f) => f.outputValuesPropertyName === initialSearchEnabledFilterPropertyName,
      );

      if (filter) {
        const filterEnabledOptionValues =
          this.initialSearch()[initialSearchEnabledFilterPropertyName];

        for (const value of filterEnabledOptionValues) {
          const option = filter.options.find((o) => o.value === value);

          if (option) {
            option.enabled = true;
          }
        }
        filter.optionsFormGroup = this.getOptionsFormGroup(filter.options.filter((o) => o.enabled));

        this.onAddFilter(filter, false);
      }
    }
  };

  private getOptionsFormGroup = (initialValues?: any[]): UntypedFormGroup =>
    new UntypedFormGroup({
      options: new UntypedFormControl(initialValues),
    });

  private setAreAllFiltersEnabled = (): void => {
    this.areAllFiltersEnabled.set(this.config().filters.every((f) => f.enabled));
  };

  private emitSearch = (): void => {
    const payload: any = {};

    if (this.currentSearchText()) {
      payload[this.config().outputSearchPropertyName] = this.currentSearchText();
    }
    const enabledFiltersWithSomeEnabledOptions = this.config().filters.filter(
      (f) => f.enabled && f.options.some((o) => o.enabled),
    );

    for (const filter of enabledFiltersWithSomeEnabledOptions) {
      payload[filter.outputValuesPropertyName] = filter.options
        .filter((o) => o.enabled)
        .map((o) => o.value);
    }
    this.searchChanged.emit(payload);
  };
}
